import { useTheme } from '@emotion/react';
import cn from 'classnames';

import { IConDefaultType } from '../types';

export enum CommentIconVariant {
  LINE = 'line',
  FILL = 'fill',
  BUBBLE_CHAT = 'bubble_chat',
  BUBBLE_CHAT_SMALL = 'bubble_chat_small',
  SIDE_MENU = 'side_menu',
}

export interface ICommentIconProps extends IConDefaultType {
  variant?: CommentIconVariant;
  onClick?: () => void;
}

function CommentIcon({
  color,
  className,
  size = 24,
  rotate,
  flip,
  variant = CommentIconVariant.LINE,
  onClick,
  style,
}: ICommentIconProps) {
  const theme = useTheme();

  return (
    <i
      className={cn('dds-icon', className, { flip })}
      style={{
        width: size,
        minWidth: size,
        height: size,
        transform: `rotate(${rotate}deg)`,
        ...style,
      }}
      onClick={() => {
        onClick && onClick();
      }}
    >
      {variant === CommentIconVariant.LINE && (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M11.9749 22zH11.8546C10.4253 21.9846 9.01636 21.6603 7.72468 21.0493C7.50795 20.9402 7.26033 20.9084 7.02299 20.9593L5.7299 21.2995C5.30465 21.4148 4.85647 21.4161 4.43054 21.3034C4.00462 21.1906 3.61602 20.9677 3.30392 20.6571C2.99182 20.3466 2.76727 19.9594 2.65291 19.5345C2.53854 19.1097 2.53841 18.6623 2.65252 18.2374L3.00336 16.9965C3.0797 16.7555 3.06179 16.4945 2.95324 16.266C2.14057 14.5411 1.83829 12.6204 2.08198 10.7298C2.32566 8.83923 3.10518 7.05746 4.3288 5.59414C5.55243 4.13082 7.16925 3.04682 8.98905 2.46969C10.8089 1.89256 12.7559 1.84631 14.6012 2.33637C16.7502 2.91854 18.6439 4.19848 19.9834 5.97423C21.3229 7.74999 22.0321 9.92046 21.9989 12.1432C21.9241 14.7597 20.8394 17.2461 18.9714 19.0829C17.1034 20.9198 14.5966 21.9649 11.9749 22ZM7.22348 18.9279C7.69153 18.9339 8.1529 19.0397 8.57672 19.2381C9.60785 19.7271 10.7331 19.9866 11.8746 19.9986H11.9749C14.0717 19.9686 16.076 19.1318 17.57 17.6628C19.0639 16.1937 19.9323 14.2057 19.9941 12.1131C20.0209 10.3355 19.4541 8.59954 18.3833 7.179C17.3124 5.75847 15.7984 4.73419 14.0799 4.26771C13.0155 3.98046 11.9037 3.91236 10.8121 4.06757C9.572 4.2482 8.3916 4.71646 7.36555 5.43477C6.3395 6.15309 5.49638 7.10147 4.9038 8.20384C4.31123 9.30621 3.98571 10.5319 3.95335 11.7825C3.92099 13.0332 4.18269 14.274 4.71747 15.4054C5.0406 16.0422 5.12589 16.773 4.95805 17.4669L4.61723 18.7578C4.5917 18.8412 4.58941 18.93 4.6106 19.0146C4.6318 19.0992 4.67567 19.1765 4.73752 19.2381C4.80198 19.299 4.88071 19.3428 4.96652 19.3656C5.05232 19.3883 5.14246 19.3892 5.2287 19.3682L6.50175 18.9979C6.73861 18.9456 6.98095 18.9221 7.22348 18.9279Z"
            fill={color || '#ADB0B8'}
          />
          <path
            d="M7.3 13.6C8.01797 13.6 8.6 13.018 8.6 12.3C8.6 11.582 8.01797 11 7.3 11C6.58203 11 6 11.582 6 12.3C6 13.018 6.58203 13.6 7.3 13.6Z"
            fill={color || '#ADB0B8'}
          />
          <path
            d="M16.8 13.6C17.518 13.6 18.1 13.018 18.1 12.3C18.1 11.582 17.518 11 16.8 11C16.082 11 15.5 11.582 15.5 12.3C15.5 13.018 16.082 13.6 16.8 13.6Z"
            fill={color || '#ADB0B8'}
          />
          <path
            d="M11.8 13.6C12.518 13.6 13.1 13.018 13.1 12.3C13.1 11.582 12.518 11 11.8 11C11.082 11 10.5 11.582 10.5 12.3C10.5 13.018 11.082 13.6 11.8 13.6Z"
            fill={color || '#ADB0B8'}
          />
        </svg>
      )}
      {variant === CommentIconVariant.FILL && (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10.4459 2.12149C11.8388 1.90243 13.2623 1.98025 14.623 2.34985C16.7677 2.93184 18.6572 4.2113 19.9929 5.98606C21.3287 7.76083 22.0344 9.9297 21.9987 12.15C21.9264 14.7861 20.8272 17.29 18.9351 19.1286C17.0431 20.9672 14.5075 21.9955 11.8682 21.9945C10.4466 21.9756 9.04546 21.654 7.75818 21.0511C7.28486 20.8108 6.74106 20.7478 6.22527 20.8735L4.78122 21.2398C4.49964 21.3167 4.20272 21.3177 3.92058 21.2429C3.63844 21.1681 3.38111 21.0201 3.17471 20.8139C2.9683 20.6077 2.82015 20.3506 2.74529 20.0686C2.67043 19.7867 2.67152 19.4901 2.74845 19.2087L3.11501 17.777C3.2477 17.2643 3.19254 16.721 2.9595 16.2454C2.10757 14.4569 1.81344 12.4539 2.11529 10.4963C2.32598 9.10323 2.82922 7.77047 3.59197 6.58553C4.35471 5.40058 5.3597 4.39025 6.54095 3.62089C7.72219 2.85153 9.05296 2.34055 10.4459 2.12149ZM8.6 12.3C8.6 13.018 8.01797 13.6 7.3 13.6C6.58203 13.6 6 13.018 6 12.3C6 11.582 6.58203 11 7.3 11C8.01797 11 8.6 11.582 8.6 12.3ZM18.1 12.3C18.1 13.018 17.518 13.6 16.8 13.6C16.082 13.6 15.5 13.018 15.5 12.3C15.5 11.582 16.082 11 16.8 11C17.518 11 18.1 11.582 18.1 12.3ZM11.8 13.6C12.518 13.6 13.1 13.018 13.1 12.3C13.1 11.582 12.518 11 11.8 11C11.082 11 10.5 11.582 10.5 12.3C10.5 13.018 11.082 13.6 11.8 13.6Z"
            fill={color || '#00C3DD'}
          />
        </svg>
      )}
      {variant === CommentIconVariant.BUBBLE_CHAT && (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">
          <path
            transform="translate(3 3)"
            d="M9.94908 0.00178071C12.0461 0.11755 14.0276 1.00227 15.5127 2.48737C16.9977 3.97251 17.8825 5.95386 17.9982 8.05094C17.9991 8.06733 18 8.08375 18 8.10016V8.54753L17.9974 8.79715C17.9639 10.0397 17.6547 11.2601 17.0938 12.3717L17.0947 12.3726C16.385 13.7925 15.294 14.9874 13.9438 15.8224C12.5941 16.657 11.0384 17.0985 9.45161 17.0994L9.44985 17.1003C8.24951 17.1031 7.06433 16.8482 5.97196 16.3576L1.18448 17.9537C0.861147 18.0615 0.504418 17.9776 0.263368 17.7367C0.0223245 17.4956 -0.0615263 17.1389 0.046274 16.8155L1.64152 12.0272C1.15086 10.9344 0.896581 9.7484 0.899707 8.54753C0.900749 6.96089 1.34316 5.40575 2.17766 4.05624C3.01264 2.70612 4.20744 1.61503 5.62742 0.905312C6.81265 0.307346 8.12229 -0.00306865 9.44985 2.28675e-05H9.89986L9.94908 0.00178071Z"
            fill={color || 'white'}
          />
        </svg>
      )}
      {variant === CommentIconVariant.BUBBLE_CHAT_SMALL && (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 10 11" fill="none">
          <path
            d="M4.16634 1.3335H5.83301C7.67397 1.3335 9.16634 2.82588 9.16634 4.66683C9.16634 6.50779 7.67397 8.00016 5.83301 8.00016V9.4585C3.74967 8.62516 0.833008 7.37516 0.833008 4.66683C0.833008 2.82588 2.32539 1.3335 4.16634 1.3335Z"
            fill={color || 'white'}
          />
        </svg>
      )}
      {variant === CommentIconVariant.SIDE_MENU && (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">
          <path
            d="M10 3H14C18.4183 3 22 6.58172 22 11C22 15.4183 18.4183 19 14 19V22.5C9 20.5 2 17.5 2 11C2 6.58172 5.58172 3 10 3ZM12 17H14C17.3137 17 20 14.3137 20 11C20 7.68629 17.3137 5 14 5H10C6.68629 5 4 7.68629 4 11C4 14.61 6.46208 16.9656 12 19.4798V17Z"
            fill={color || 'black'}
          />
        </svg>
      )}
    </i>
  );
}

export default CommentIcon;
